'use client'

import { useState, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { ChevronLeft, Loader2, Camera, X } from 'lucide-react'
import { useTheme } from '@/contexts/ThemeContext'
import { getSupabaseClient } from '@/utils/supabase/singleton'
import { parseAuthToken } from '@/utils/base64'
import { compressImage, validateImageFile, revokeImagePreview } from '@/utils/imageCompression'

interface FormData {
  title: string
  content: string
  category: string
  is_private: boolean
  images: File[]
  image_previews: string[]
}

const categories = [
  { value: 'bug', label: '🐛 버그 제보', description: '오류나 문제 발생' },
  { value: 'feature', label: '💡 기능 요청', description: '새로운 기능 제안' },
  { value: 'question', label: '❓ 질문', description: '사용법이나 기능 문의' },
  { value: 'other', label: '📝 기타', description: '기타 문의사항' }
]

export default function NewInquiryPage() {
  const router = useRouter()
  const { theme } = useTheme()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const imageInputRef = useRef<HTMLInputElement>(null)

  const isDark = theme === 'dark'

  const [formData, setFormData] = useState<FormData>({
    title: '',
    content: '',
    category: 'bug',
    is_private: false,
    images: [],
    image_previews: []
  })

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])

    if (formData.images.length + files.length > 3) {
      setError('최대 3장까지 업로드 가능합니다.')
      return
    }

    setError('')
    const newImages: File[] = []
    const newPreviews: string[] = []

    for (const file of files) {
      const validation = validateImageFile(file)
      if (!validation.valid) {
        setError(validation.error || '이미지를 처리할 수 없습니다.')
        continue
      }

      try {
        const compressedFile = await compressImage(file, {
          maxWidth: 1920,
          maxHeight: 1920,
          quality: 0.85,
          maxSizeMB: 2
        })

        newImages.push(compressedFile)
        newPreviews.push(URL.createObjectURL(compressedFile))
      } catch {
        setError('이미지 처리 중 오류가 발생했습니다.')
      }
    }

    setFormData({
      ...formData,
      images: [...formData.images, ...newImages],
      image_previews: [...formData.image_previews, ...newPreviews]
    })
  }

  const removeImage = (index: number) => {
    const newImages = formData.images.filter((_, i) => i !== index)
    const newPreviews = formData.image_previews.filter((_, i) => i !== index)

    revokeImagePreview(formData.image_previews[index])

    setFormData({
      ...formData,
      images: newImages,
      image_previews: newPreviews
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.title.trim() || !formData.content.trim()) {
      setError('제목과 내용을 입력해주세요.')
      return
    }

    setIsSubmitting(true)
    setError('')

    try {
      const token = localStorage.getItem('auth_token')
      if (!token) {
        alert('로그인이 필요합니다')
        router.push('/login')
        return
      }

      const supabase = getSupabaseClient()

      // 토큰 디코드 - parseAuthToken이 JWT/Base64 모두 처리
      const payload = parseAuthToken(token)
      if (!payload) {
        throw new Error('토큰 파싱에 실패했습니다. 다시 로그인해주세요.')
      }

      // user_id 추출 - 토큰에 직접 있거나, kakao_id/naver_id로 조회
      let userId = payload.user_id

      if (!userId) {
        // kakao_id 또는 naver_id로 user_profiles에서 id 조회
        let userQuery = supabase.from('user_profiles').select('id')
        if (payload.kakao_id) {
          userQuery = userQuery.eq('kakao_id', payload.kakao_id)
        } else if (payload.naver_id) {
          userQuery = userQuery.eq('naver_id', payload.naver_id)
        } else {
          throw new Error('로그인 정보를 찾을 수 없습니다')
        }

        const { data: userData, error: userError } = await userQuery.single()
        if (userError || !userData) {
          throw new Error('사용자 정보를 찾을 수 없습니다')
        }
        userId = userData.id
      }

      // 이미지 업로드 먼저 처리
      const imageUrls: string[] = []
      if (formData.images.length > 0) {
        for (let i = 0; i < formData.images.length; i++) {
          const image = formData.images[i]
          const fileExt = image.name.split('.').pop()
          const fileName = `inquiry_${userId}_${Date.now()}_${i + 1}.${fileExt}`

          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('inquiry-images')
            .upload(fileName, image, { upsert: true })

          if (uploadError) {
            if (uploadError.message.includes('not found') || uploadError.message.includes('Bucket')) {
              setError('이미지 저장소가 준비되지 않았습니다. 관리자에게 문의해주세요.')
            }
            continue
          }

          if (uploadData) {
            const { data: { publicUrl } } = supabase.storage
              .from('inquiry-images')
              .getPublicUrl(fileName)

            imageUrls.push(publicUrl)
          }
        }
      }

      const { error: insertError } = await supabase
        .from('inquiries')
        .insert({
          user_id: userId,
          title: formData.title.trim(),
          content: formData.content.trim(),
          category: formData.category,
          status: 'pending',
          is_private: formData.is_private,
          image_urls: imageUrls.length > 0 ? imageUrls : null
        })

      if (insertError) throw insertError

      router.push('/community/inquiry')
    } catch (err) {
      console.error('문의글 작성 실패:', err)
      setError(err instanceof Error ? err.message : '문의글 작성 중 오류가 발생했습니다.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* 헤더 */}
      <div className={`sub-sticky z-20 px-3 xxs:px-2 py-2.5 xxs:py-2 border-b ${
        isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'
      }`}>
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          <button
            onClick={() => router.back()}
            className={`flex items-center gap-0.5 text-xs xxs:text-[11px] ${
              isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <ChevronLeft className="w-4 h-4 xxs:w-3.5 xxs:h-3.5" />
            뒤로
          </button>
          <h1 className={`text-base xxs:text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            문의하기
          </h1>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !formData.title.trim() || !formData.content.trim()}
            className={`px-3 xxs:px-2.5 py-1.5 xxs:py-1 text-xs xxs:text-[11px] font-medium rounded-full transition-all ${
              isSubmitting || !formData.title.trim() || !formData.content.trim()
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-blue-500 text-white hover:bg-blue-600'
            }`}
          >
            {isSubmitting ? <Loader2 className="w-3.5 h-3.5 xxs:w-3 xxs:h-3 animate-spin" /> : '등록'}
          </button>
        </div>
      </div>

      {/* 폼 */}
      <form onSubmit={handleSubmit} className="max-w-2xl mx-auto px-3 xxs:px-2 py-3 xxs:py-2">
        {error && (
          <div className={`mb-3 xxs:mb-2 p-2.5 xxs:p-2 rounded-lg text-xs xxs:text-[11px] ${
            isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-600'
          }`}>
            {error}
          </div>
        )}

        {/* 카테고리 선택 */}
        <div className="mb-3 xxs:mb-2">
          <label className={`block text-xs xxs:text-[11px] font-medium mb-1.5 xxs:mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            문의 유형
          </label>
          <div className="grid grid-cols-2 gap-1.5 xxs:gap-1">
            {categories.map((cat) => (
              <button
                key={cat.value}
                type="button"
                onClick={() => setFormData({ ...formData, category: cat.value })}
                className={`p-2.5 xxs:p-2 rounded-xl border text-left transition-all ${
                  formData.category === cat.value
                    ? isDark
                      ? 'border-blue-500 bg-blue-900/30'
                      : 'border-blue-500 bg-blue-50'
                    : isDark
                      ? 'border-gray-700 bg-gray-800 hover:border-gray-600'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className={`text-xs xxs:text-[11px] font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {cat.label}
                </div>
                <div className={`text-[10px] xxs:text-[9px] mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {cat.description}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* 제목 */}
        <div className="mb-3 xxs:mb-2">
          <label className={`block text-xs xxs:text-[11px] font-medium mb-1.5 xxs:mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            제목
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="문의 제목을 입력하세요"
            maxLength={200}
            className={`w-full px-3 xxs:px-2.5 py-2.5 xxs:py-2 text-sm xxs:text-xs rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              isDark
                ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
            }`}
          />
          <div className={`text-right text-[10px] xxs:text-[9px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {formData.title.length}/200
          </div>
        </div>

        {/* 내용 */}
        <div className="mb-3 xxs:mb-2">
          <label className={`block text-xs xxs:text-[11px] font-medium mb-1.5 xxs:mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            내용
          </label>
          <textarea
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            placeholder={`문의 내용을 자세히 작성해주세요.\n\n버그 제보시:\n- 어떤 상황에서 문제 발생?\n- 기대 동작 vs 실제 동작\n- 사용 기기/브라우저`}
            rows={10}
            maxLength={5000}
            className={`w-full px-3 xxs:px-2.5 py-2.5 xxs:py-2 text-xs xxs:text-[11px] rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${
              isDark
                ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
            }`}
            style={{ minHeight: '180px' }}
          />
          <div className={`text-right text-[10px] xxs:text-[9px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {formData.content.length}/5000
          </div>
        </div>

        {/* 이미지 업로드 */}
        <div className={`mb-3 xxs:mb-2 p-2.5 xxs:p-2 rounded-xl border ${
          isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <label className={`block text-xs xxs:text-[11px] font-medium mb-2 xxs:mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            📷 스크린샷 첨부 (최대 3장)
          </label>

          <div className="grid grid-cols-4 gap-1.5 xxs:gap-1 mb-2 xxs:mb-1.5">
            {formData.image_previews.map((preview, index) => (
              <div key={index} className="relative aspect-square">
                <img
                  src={preview}
                  alt={`미리보기 ${index + 1}`}
                  className="w-full h-full object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 shadow-md"
                >
                  <X className="w-3 h-3 xxs:w-2.5 xxs:h-2.5" />
                </button>
              </div>
            ))}
            {formData.images.length < 3 && (
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                className={`aspect-square rounded-lg border-2 border-dashed flex items-center justify-center transition-colors ${
                  isDark
                    ? 'border-gray-600 hover:border-blue-500 hover:bg-gray-700'
                    : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                }`}
              >
                <Camera className={`w-5 h-5 xxs:w-4 xxs:h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              </button>
            )}
          </div>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageSelect}
            className="hidden"
          />
          <p className={`text-[10px] xxs:text-[9px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            버그 제보시 스크린샷을 첨부하면 더 빠른 해결이 가능합니다
          </p>
        </div>

        {/* 비밀글 옵션 */}
        <div className="mb-3 xxs:mb-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_private}
              onChange={(e) => setFormData({ ...formData, is_private: e.target.checked })}
              className="w-4 h-4 xxs:w-3.5 xxs:h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className={`text-xs xxs:text-[11px] ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              🔒 비밀글로 작성
            </span>
            <span className={`text-[10px] xxs:text-[9px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              (본인과 관리자만 볼 수 있음)
            </span>
          </label>
        </div>

        {/* 안내 */}
        <div className={`p-2.5 xxs:p-2 rounded-lg text-[10px] xxs:text-[9px] ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
          <p>📌 문의 내용은 관리자가 확인 후 답변드립니다.</p>
          <p className="mt-0.5">📌 개인정보(연락처 등)는 작성하지 마세요.</p>
        </div>

        <div className="h-16" />
      </form>
    </div>
  )
}
